import React from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { PipelineTargetTable, PipelineStep } from './types';
import { EXPR_COLOR, DIRECT_COLOR, TARGET_COLOR } from './types';

interface PipelineNodeDetailsProps {
  target: PipelineTargetTable | null;
  onClose?: () => void;
}

const StepRow: React.FC<{ step: PipelineStep; index: number }> = ({ step, index }) => {
  const isExpr = step.operation === 'expression';
  const chipColor = isExpr ? EXPR_COLOR : DIRECT_COLOR;
  const chipLabel = isExpr ? 'EXPR' : 'DIRECT';
  const confPct = Math.round(step.confidence * 100);

  // Format input → output
  const inputCol = step.inputs?.[0]?.split('.').pop() ?? '—';
  const outputCol = step.outputs?.[0]?.split('.').pop() ?? '—';

  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        '&:hover': { bgcolor: '#f8fafc' },
        borderBottom: '1px solid #f1f5f9',
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Chip
          label={chipLabel}
          size="small"
          sx={{
            height: 18,
            fontSize: 9,
            fontWeight: 700,
            bgcolor: `${chipColor}18`,
            color: chipColor,
            borderRadius: '4px',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Typography variant="caption" sx={{ fontSize: 11, color: '#475569', flex: 1 }} noWrap>
          {inputCol}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 11, color: '#94a3b8' }}>→</Typography>
        <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11, color: '#1e293b' }} noWrap>
          {outputCol}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 10, color: confPct >= 80 ? '#22c55e' : '#f59e0b' }}>
          {confPct}%
        </Typography>
      </Stack>
      {isExpr && step.expression && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontSize: 10,
            color: '#64748b',
            fontFamily: 'monospace',
            mt: 0.25,
            pl: 4.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {step.expression}
        </Typography>
      )}
    </Box>
  );
};

const PipelineNodeDetails: React.FC<PipelineNodeDetailsProps> = ({ target, onClose }) => {
  if (!target) return null;

  const confPct = Math.round(target.avgConfidence * 100);

  return (
    <Box
      sx={{
        width: 340,
        minWidth: 340,
        borderLeft: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflowY: 'auto',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TARGET_COLOR, flexShrink: 0 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: 13, flex: 1 }} noWrap>
            {target.name}
          </Typography>
          {onClose && (
            <Tooltip title="Close" arrow>
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close target details"
                sx={{ p: 0.25, color: '#94a3b8', '&:hover': { color: '#64748b' } }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: 11 }}>
            {target.stepCount} mappings
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: 11 }}>
            · Avg {confPct}% confidence
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
          {target.directCount > 0 && (
            <Chip
              label={`${target.directCount} direct`}
              size="small"
              sx={{ height: 20, fontSize: 10, bgcolor: `${DIRECT_COLOR}14`, color: DIRECT_COLOR }}
            />
          )}
          {target.expressionCount > 0 && (
            <Chip
              label={`${target.expressionCount} expression`}
              size="small"
              sx={{ height: 20, fontSize: 10, bgcolor: `${EXPR_COLOR}14`, color: EXPR_COLOR }}
            />
          )}
        </Stack>
      </Box>

      {/* Field Mappings */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#64748b',
            px: 1.5,
            py: 0.75,
            display: 'block',
            borderBottom: '1px solid #f1f5f9',
            fontSize: 10,
          }}
        >
          Field Mappings
        </Typography>
        {target.steps.map((step, i) => (
          <StepRow key={step.stepId} step={step} index={i} />
        ))}
      </Box>
    </Box>
  );
};

export default PipelineNodeDetails;
